import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import * as myGlobals from 'src/app/globals';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  constructor(private http: HttpClient) {}

  private get storageKeyPrefix(): string {
    if (myGlobals.userIdentity === undefined) {
      return '';
    } else {
      return `cachedData-${myGlobals.userIdentity.CompanyDB}-${myGlobals.userIdentity.RoleId}-`;
    }
  }

  private fetchDataFromApi(url: string): Observable<any> {
    return this.http.get(url);
  }

  private getValueFromSessionStorage(key: string): Observable<any> {
    const fullKey = this.storageKeyPrefix + key;
    try {
      const data = sessionStorage.getItem(fullKey);
      if (data !== null) {
        return of(JSON.parse(data));
      } else {
        return throwError('Key does not exist in sessionStorage');
      }
    } catch (error) {
      console.error('Error getting value from sessionStorage:', error);
      return of(undefined);
    }
  }

  private setValueInSessionStorage(key: string, value: any): Observable<void> {
    const fullKey = this.storageKeyPrefix + key;
    try {
      sessionStorage.setItem(fullKey, JSON.stringify(value));
      return of(undefined);
    } catch (error) {
      console.error('Error setting value in sessionStorage:', error);
      return of(undefined);
    }
  }

  clearDatabase(): Observable<void> {
    // Clears all data from sessionStorage that matches our prefix
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.storageKeyPrefix)) {
          sessionStorage.removeItem(key);
        }
      }
      return of(undefined);
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
      return throwError('Failed to clear session storage');
    }
  }

  getData(url: string): Observable<any> {
    return this.getValueFromSessionStorage(url).pipe(
      switchMap((value) => {
        if (value !== undefined) {
          return of(value); // Emit value if it exists in sessionStorage
        } else {
          return this.fetchDataFromApi(url).pipe(
            switchMap((data) => {
              return this.setValueInSessionStorage(url, data).pipe(
                catchError(() => of(undefined)), // Fallback if storing in sessionStorage fails
                switchMap(() => of(data))
              );
            })
          );
        }
      }),
      catchError(() => {
        // If key doesn't exist in sessionStorage, we fetch from API
        return this.fetchDataFromApi(url).pipe(
          switchMap((data) => {
            return this.setValueInSessionStorage(url, data).pipe(
              catchError(() => of(undefined)),
              switchMap(() => of(data))
            );
          })
        );
      })
    );
  }
}
